/**
 * pendingGroupJoin — durable hand-off of a group-invite token across the
 * signup → (email verification) → sign-in round trip (operator Q1, R1 wave 2).
 *
 * ── Why this exists (persistence choice, stated for the handoff) ───────────────
 * On prod REQUIRE_EMAIL_VERIFICATION is ON, so /auth/signup does NOT return a
 * session — the new user leaves to their inbox and clicks a Better Auth
 * verification link that may open in a DIFFERENT tab or hours later. That link
 * (/sign-in?verified=1) carries no group token. To auto-file the join request
 * once the session finally lands, the token must survive OUTSIDE the URL.
 *
 * Chosen persistence: localStorage with a bounded TTL (24h — comfortably longer
 * than the ~15-min email-verification window, short enough that a stale entry
 * self-expires). On dev (verification OFF) the session lands immediately at
 * signup and the same store drives the post-signup redirect — one code path for
 * both environments.
 *
 * ── Security note ──────────────────────────────────────────────────────────────
 * The token is a bearer credential, already present in the /app/join/<token>
 * URL (which sets Referrer-Policy: no-referrer, routes/join.tsx). Persisting it
 * in localStorage rather than threading it through the /sign-up + verification
 * URLs keeps it off the signup page's address bar (and out of the Turnstile
 * script's Referer header). It is cleared the moment it is consumed.
 *
 * ── Degradation ────────────────────────────────────────────────────────────────
 * localStorage can be unavailable (Safari private mode, sandboxed iframe). Every
 * access is guarded; on failure the store no-ops and the flow degrades to the
 * URL returnTo path (which lands the user on the join page, where they can file
 * the request manually).
 */

const KEY = "proof.pending-group-join";
const TTL_MS = 24 * 60 * 60 * 1000;

interface Stored {
  token: string;
  expiresAt: number;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Remember a group-invite token so it survives the signup/verification round trip. */
export function storePendingGroupJoin(token: string): void {
  if (!token) return;
  const s = storage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify({ token, expiresAt: Date.now() + TTL_MS } satisfies Stored));
  } catch (err) {
    // Quota / disabled storage — degrade to the URL returnTo path. Not a
    // user-data mutate; log at debug so the degradation is observable.
    console.debug({ event: "pending_group_join_store_failed", err });
  }
}

/** Read the pending token if present and unexpired; clears (and returns null) otherwise. */
export function readPendingGroupJoin(): string | null {
  const s = storage();
  if (!s) return null;
  let raw: string | null = null;
  try {
    raw = s.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (typeof parsed.token !== "string" || typeof parsed.expiresAt !== "number") {
      clearPendingGroupJoin();
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      clearPendingGroupJoin();
      return null;
    }
    return parsed.token;
  } catch {
    clearPendingGroupJoin();
    return null;
  }
}

/** Forget any pending group-invite token. */
export function clearPendingGroupJoin(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch (err) {
    // Disabled storage — the entry (if any) will self-expire via TTL.
    console.debug({ event: "pending_group_join_clear_failed", err });
  }
}

/**
 * Post-auth destination resolver shared by /sign-in and /sign-up. If a group
 * invite is pending (the signup-via-group-invite path), consume it and route to
 * the join page with the auto-file flag; otherwise fall back to the caller's
 * already-sanitised returnTo. `viaSignup=1` tells JoinView to file the join
 * request automatically (operator Q1: signup-via-group-invite auto-FILES;
 * the owner still approves).
 */
export function resolvePostAuthRedirect(fallback: string): string {
  const token = readPendingGroupJoin();
  if (token) {
    clearPendingGroupJoin();
    return `/app/join/${encodeURIComponent(token)}?viaSignup=1`;
  }
  return fallback;
}
